import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {accountThirdPartyLogoFailed} from '../../../store/data/accounts/accountsActions';
import {TooltipExplicitCondition} from '../tooltips/tooltipExplicitCondition';
import {Avatar, AvatarProps} from './avatar';
import {AvatarBackground} from './avatarBackground';

/*
 * Constants.
 */

const thirdPartyLogoApi = 'https://logo.clearbit.com/';

/*
 * Types.
 */

interface AccountAvatarProps extends AvatarProps {
  className?: string;
  account?: AccountModel;
  avatarUrl?: string;
  avatarIconSize: number;
  avatarIconColor?: string;
  placeholder?: ReactNode;
}

/*
 * Styles.
 */

const StyledAvatarIconDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  position: relative;
  width: 100%;
  height: 100%;
`;

const StyledImg = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;

  // Reduces some blurriness that occurs.
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
`;

const StyledImageDetailsDiv = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 8%);
  border-radius: 50%;
  overflow: hidden;
`;

const StyledTooltipAccountNameDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

const StyledClearbitDisclaimerDiv = styled.div`
  display: inline-block;
  size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.alphas.white50};
`;

const StyledClearbitLinkDiv = styled(StyledClearbitDisclaimerDiv)`
  text-decoration: underline;
  margin-left: 4px;
`;

/*
 * Component.
 */

export const AccountAvatar: FC<AccountAvatarProps> = (props) => {
  const theme = useUserTheme();
  const {
    className,
    account,
    size,
    avatarUrl,
    avatarIconSize,
    borderColor,
    avatarIconColor = theme.greys.shade60,
    placeholder,
  } = props;
  const dispatch = useAppDispatch();

  // You can either get the avatarUrl via the AccountModel or have it explicitly passed in via props.
  // This is because not all accounts will be loaded in Redux but you may have the avatarUrl regardless via an API call.
  const accountAvatarUrl = computeAccountAvatarUrl(account, avatarUrl);

  const onImageLoadError = () => {
    if (!account) {
      return;
    }
    dispatch(accountThirdPartyLogoFailed(account.id));
  };

  return (
    <Avatar className={className} size={size} borderColor={borderColor}>
      {renderAvatarImageOrIcon(
        accountAvatarUrl,
        avatarIconSize,
        avatarIconColor,
        account,
        onImageLoadError,
        placeholder,
      )}
    </Avatar>
  );
};

/*
 * Helpers.
 */

function computeAccountAvatarUrl(account?: AccountModel, avatarUrl?: string) {
  if (account?.avatarUrl) {
    return account.avatarUrl;
  }
  if (avatarUrl) {
    return avatarUrl;
  }
  if (account && account.domains.length > 0 && !account.shouldIgnoreThirdPartyLogo) {
    return `${thirdPartyLogoApi}${account.domains[0]}`;
  }
  return undefined;
}

function renderAvatarImageOrIcon(
  avatarUrl: string | undefined,
  avatarIconSize: number,
  avatarIconColor: string,
  account: AccountModel | undefined,
  onError: Handler,
  placeholder?: ReactNode,
) {
  if (!avatarUrl) {
    return (
      placeholder ?? (
        <>
          <UserThemeConsumer>{(theme) => <AvatarBackground color={theme.greys.shade30} />}</UserThemeConsumer>
          <StyledAvatarIconDiv>
            <Icon name="briefcaseFilled" size={avatarIconSize} color={avatarIconColor} />
          </StyledAvatarIconDiv>
        </>
      )
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledTooltipCoordinator
          render={() => renderTooltip(account?.name ?? '', avatarUrl)}
          placement="top"
          // Only need persistent for 3rd-party so the user can click on attribution link.
          persistent={avatarUrl.includes(thirdPartyLogoApi)}
        >
          <TooltipExplicitCondition isEnabled={Boolean(account?.name) || Boolean(avatarUrl)} />
          <AvatarBackground color={theme.greys.white} />
          {/* We need to key the image so react knows to throw it away since the image sticks around causing flickering. */}
          <StyledImg key={avatarUrl} src={avatarUrl} onError={onError} />
          <StyledImageDetailsDiv />
        </StyledTooltipCoordinator>
      )}
    </UserThemeConsumer>
  );
}

function renderTooltip(accountName: string, avatarUrl: string) {
  // If we are using a 3rd-party logo, add link back to the 3rd party.
  if (avatarUrl.includes(thirdPartyLogoApi)) {
    return (
      <>
        {accountName && <StyledTooltipAccountNameDiv>{accountName}</StyledTooltipAccountNameDiv>}
        <StyledClearbitDisclaimerDiv>
          <Trans id="account-avatar-clearbit-disclaimer">Logo provided by</Trans>
          <StyledClearbitLinkDiv onClick={() => openInBrowser('https://clearbit.com')}>
            Clearbit
          </StyledClearbitLinkDiv>
        </StyledClearbitDisclaimerDiv>
      </>
    );
  }
  // If the user supplied their own avatar, just show the name of the account.
  return <StyledTooltipAccountNameDiv>{accountName}</StyledTooltipAccountNameDiv>;
}
